import dayjs from 'dayjs';
import { Payload } from 'recharts/types/component/DefaultLegendContent';
import type {
  AllocationOfCostsCategory,
  AllocationOfCostsChartResponseByCategory,
  ViewBy,
} from '../../../../api/financials-client/financials-client.type';
import { snakeCaseToTitleCase } from '../../../../helpers/string-helpers/string-helpers';
import type { AllocationOfCostsMeasures } from '../../../../store/financials-store/financials-store.type';
import { getItemColor } from '../cost-by-initiative/cost-by-initiative.helpers';

type FilteredAllocationOfCostsData = {
  [dateKey: string]:
    | {
        [K in AllocationOfCostsCategory]: {
          [viewBy in ViewBy]: number;
        };
      }
    | {};
};

type DataPoint = {
  date: string;
  [key: string]: string | number;
};

/**
 * Filters the allocation of costs data based on the selected measure, options, and team.
 *
 * @param {AllocationOfCostsChartResponseByCategory | undefined} data - The allocation of costs data
 * @param {AllocationOfCostsMeasures} selectedMeasure - The selected measure
 * @param {string[]} selectedOptions - The selected options
 */
function filterAllocationOfCostsData(
  data: AllocationOfCostsChartResponseByCategory | undefined,
  selectedMeasure: AllocationOfCostsMeasures,
  selectedOptions: string[],
  team: string | null,
): FilteredAllocationOfCostsData | undefined {
  if (data && selectedMeasure in data) {
    return Object.fromEntries(
      Object.entries(data[selectedMeasure].chart_data).map(([dateKey, options]) => {
        const filteredOptions = Object.fromEntries(
          Object.entries(options)
            .filter(([option, _]) => {
              return selectedOptions.includes(option);
            })
            .map(([option, optionValues]) => {
              if (team) {
                // If team is specified, find that specific team's data
                for (const [_, teamData] of Object.entries(optionValues.teams)) {
                  if (teamData.id === team) {
                    // Return the team data directly (without the team name or ID nesting)
                    const { id: _, ...teamStats } = teamData;
                    return [option, teamStats];
                  }
                }
                // If team wasn't found, return empty object
                return [option, {}];
              } else {
                // If no team specified, return the total stats directly
                return [option, optionValues.total];
              }
            }),
        );

        return [dateKey, filteredOptions];
      }),
    );
  }

  return {};
}

/**
 * Transforms the filtered allocation of costs data into a new format for consumption by the chart.
 *
 * @param {FilteredAllocationOfCostsData} data - The filtered allocation of costs data
 * @param {ViewBy} viewBy - The viewBy to transform the data by
 * @return {Array<Record<string, string | number>>} The transformed data
 */
function transformAllocationOfCostsData(
  data: FilteredAllocationOfCostsData | undefined,
  viewBy: 'tasks' | 'points',
): DataPoint[] | [] {
  if (!data) {
    return [];
  }

  return Object.entries(data).map(([dateKey, selectedOptionsData]) => ({
    date: dateKey,
    ...Object.fromEntries(
      Object.entries(selectedOptionsData).map(([option, optionData]) => {
        return [option, optionData[viewBy]];
      }),
    ),
  }));
}

/**
 * Builds the trend lines for the allocation of costs chart using least squares regression
 *
 * @param {DataPoint[]} data - The data to build the trend lines for
 * @return {DataPoint[]} The data with trend lines
 */
function addTrendLines(data: DataPoint[] | []): DataPoint[] | [] {
  if (!data || data.length <= 1) {
    return data;
  }

  // Clone the data to avoid modifying the original
  const result = JSON.parse(JSON.stringify(data));

  // Get all numeric keys except 'date'
  const keys = Object.keys(data[0]).filter((key) => {
    return key !== 'date' && typeof data[0][key as keyof Record<string, string | number>] === 'number';
  });

  // Convert dates to numerical values (months since first date) for calculation
  const firstDate = dayjs(data[0].date).startOf('month');
  const xValues = data.map((point) => {
    const date = dayjs(point.date).startOf('month');
    return date.diff(firstDate, 'month', true); // Using true for floating point months
  });

  // For each key, calculate and add the trendLine
  keys.forEach((key) => {
    // Get y values for current key
    const yValues = data.map((point) => Number(point[key as keyof Record<string, string | number>] || 0));

    // Calculate mean of x and y values
    const xMean = xValues.reduce((sum, x) => sum + x, 0) / xValues.length;
    const yMean = yValues.reduce((sum, y) => sum + y, 0) / yValues.length;

    // Calculate slope (m) and y-intercept (b) using least squares method
    let numerator = 0;
    let denominator = 0;

    for (let i = 0; i < data.length; i++) {
      const xDiff = xValues[i] - xMean;
      const yDiff = (yValues[i] as number) - yMean;
      numerator += xDiff * yDiff;
      denominator += xDiff * xDiff;
    }

    const slope = denominator !== 0 ? numerator / denominator : 0;
    const intercept = yMean - slope * xMean;

    // Generate trendLine values
    const trendLineKey = `${key}_trend`;
    for (let i = 0; i < result.length; i++) {
      result[i][trendLineKey] = Math.round(slope * xValues[i] + intercept);
    }
  });

  return result;
}

/**
 * Builds the options for the measure select dropdown.
 *
 * @param {AllocationOfCostsChartResponseByCategory} chartData - The chart data
 * @return {Array<{ label: string; value: AllocationOfCostsMeasures }>} The options
 */
function buildMeasureSelectOptions(
  chartData: AllocationOfCostsChartResponseByCategory,
): { label: string; value: AllocationOfCostsMeasures }[] {
  return Object.keys(chartData)
    .sort((a, b) => {
      return a.localeCompare(b);
    })
    .map((key) => ({
      label: snakeCaseToTitleCase(key),
      value: key as AllocationOfCostsMeasures,
    }));
}

/**
 * Helper function to build the legend payload for the allocation of costs chart
 *
 * @param {string[]} ids - The IDs to include in the legend
 * @param {string[]} colorsList - The list of colors to use for the legend items
 * @returns {Payload[]} The legend payload for the chart
 */
const buildChartLegend = (ids: string[], colorsList: string[]): Payload[] => {
  return ids.map((id, _, ids) => ({
    id,
    value: id,
    color: getItemColor(id, ids, colorsList),
  }));
};

/**
 * Returns the icon name for a given measure
 *
 * @param {AllocationOfCostsMeasures} measure - The measure
 * @returns {string} The icon name
 */
const getIconNameForMeasure = (measure: AllocationOfCostsMeasures): string => {
  switch (measure) {
    case 'epic':
      return 'auto_stories';
    case 'type':
      return 'category';
    case 'label':
      return 'label';
    case 'points':
      return 'pin';
    case 'sprint':
      return 'directions_run';
    case 'assignee':
      return 'person';
    case 'due_date':
      return 'event';
    case 'priority':
      return 'priority_high';
    case 'reporter':
      return 'person';
    case 'component':
      return 'code';
    case 'current_section':
      return 'table_chart';
    default:
      return 'adjust';
  }
};

export {
  addTrendLines,
  buildChartLegend,
  buildMeasureSelectOptions,
  filterAllocationOfCostsData,
  getIconNameForMeasure,
  transformAllocationOfCostsData,
};
